import { FC, memo, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthFetchFromRestAPI } from '../../../util/api';
import { Alert, ButtonGroup, Spinner } from 'react-bootstrap';
import ToggleButton from 'react-bootstrap/ToggleButton';
import i18next from 'i18next';
import {
  StatisticPerDayDto,
  StatisticsPerPatientDto,
  StatisticsTotalDto
} from '../../../../../shared/dtos/statistics.dto';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, TooltipProps, XAxis, YAxis } from 'recharts';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { formatHours } from '../../../util/duration-utils';

enum CHARTS {
  HOURS_PER_DAY = 'HOURS_PER_DAY',
  HOURS_PER_PATIENT = 'HOURS_PER_PATIENT',
}

const dateFormatter: Intl.DateTimeFormat = new Intl.DateTimeFormat('de-AT', {
  weekday: 'short',
  day: '2-digit',
  month: 'short',
});

const formatDayLabel = (dateString: string): string => {
  const date: Date = new Date(dateString);
  return isNaN(date.getTime()) ? String(dateString) : dateFormatter.format(date);
};

const CustomTooltip: FC<TooltipProps<number, string>> = ({ active, payload, label }: TooltipProps<number, string>): ReactElement | null => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const firstElement = payload[0];
  const hourValue: number = typeof firstElement.value === 'number' ? firstElement.value : Number(firstElement.value);
  const totalMinutes: number = Math.round(hourValue * 60);
  const hours: number = Math.floor(totalMinutes / 60);
  const minutes: number = totalMinutes % 60;

  const formatted: string = `${hours}h ${minutes.toString().padStart(2, '0')}min`;

  return (
    <div className="card shadow-sm border bg-body rounded-3 p-2">
      <div className="card-body p-2">
        <div className="fw-semibold mb-1">{label}</div>
        <div className="text-muted small">
          {i18next.t('teleTherapy.duration')}: <span className="fw-bold text-dark">{formatted}</span>
        </div>
      </div>
    </div>
  );
};

const TeleTherapy: FC = (): ReactElement => {
  const { authFetch } = useAuthFetchFromRestAPI();

  const [activeChart, setActiveChart] = useState<CHARTS>(CHARTS.HOURS_PER_DAY);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [byDay, setByDay] = useState<StatisticPerDayDto[]>([]);
  const [byPatient, setByPatient] = useState<StatisticsPerPatientDto[]>([]);
  const [total, setTotal] = useState<StatisticsTotalDto | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const overview = await authFetch('/api/v1/statistics/overview', { method: 'GET' });

      setByDay(overview.byDay);
      setByPatient(overview.byPatient);
      setTotal(overview.totals);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const dayData = useMemo(() => byDay.map((value) => ({ ...value, dayLabel: formatDayLabel(value.day) })), [byDay]);

  const patientChartHeight: number = Math.max(320, byPatient.length * 44 + 80);

  const renderChart = (): ReactElement => {
    if (isLoading) {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ height: 360 }}>
          <Spinner animation="border" role="status" />
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 120 }}>
          <Alert variant="danger" className="mb-0">{errorMessage}</Alert>
        </div>
      );
    }

    if (activeChart === CHARTS.HOURS_PER_DAY) {
      return dayData.length > 0 ? (
        <div className="d-flex justify-content-center" style={{ width: '100%' }}>
          <div style={{ width: '80%', maxWidth: 1920 }}>
            <ResponsiveContainer width="100%" height={420}>
              <BarChart
                data={dayData}
                margin={{ top: 10, right: 24, left: 8, bottom: 28 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="dayLabel" />
                <YAxis name="hours" unit="h" type="number" />
                <ChartTooltip content={<CustomTooltip />}/>
                <Bar dataKey="durationInHours" radius={[6, 6, 0, 0]} fill="#FFAB24"/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div className="text-center mt-3">{i18next.t('teleTherapy.noData')}</div>
      );
    }

    return byPatient.length > 0 ? (
      <div className="d-flex justify-content-center" style={{ width: '100%' }}>
        <div style={{ width: '80%', maxWidth: 1920 }}>
          <ResponsiveContainer width="100%" height={patientChartHeight}>
            <BarChart
              data={byPatient}
              layout="vertical"
              margin={{ top: 10, right: 24, left: 8, bottom: 28 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis name="hours" unit="h" type="number" />
              <YAxis dataKey="patient" type="category" width={140} />
              <ChartTooltip
                content={<CustomTooltip />}
                labelFormatter={(label, payload) =>
                  payload && payload[0] && typeof payload[0].payload?.patient === 'string' ? payload[0].payload.patient : String(label)
                }/>
              <Bar dataKey="durationInHours" radius={[0, 6, 6, 0]} fill="#FFAB24" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    ) : (
      <div className="text-center mt-3">{i18next.t('teleTherapy.noData')}</div>
    );
  };

  return (
    <div className="w-100">
      <div className="d-flex justify-content-end mt-3 me-3">
        <ButtonGroup role="group">
          <ToggleButton
            id="hours-per-day"
            type="radio"
            variant={activeChart === CHARTS.HOURS_PER_DAY ? 'dark' : 'outline-dark'}
            name="chart"
            value={CHARTS.HOURS_PER_DAY}
            checked={activeChart === CHARTS.HOURS_PER_DAY}
            onChange={() => setActiveChart(CHARTS.HOURS_PER_DAY)}>
            <i className="fas fa-chart-bar" />
          </ToggleButton>

          <ToggleButton
            id="hours-per-patient"
            type="radio"
            variant={activeChart === CHARTS.HOURS_PER_PATIENT ? 'dark' : 'outline-dark'}
            name="chart"
            value={CHARTS.HOURS_PER_PATIENT}
            checked={activeChart === CHARTS.HOURS_PER_PATIENT}
            onChange={() => setActiveChart(CHARTS.HOURS_PER_PATIENT)}>
            <i className="fas fa-list" />
          </ToggleButton>
        </ButtonGroup>
      </div>

      <OverlayTrigger
        placement="top"
        delay={{ show: 150, hide: 100 }}
        overlay={
          <Tooltip>{formatHours(total?.totalHours ?? 0)}</Tooltip>
        }>
        <div className="py-3 text-center">
          <div className="fw-bold" style={{ fontSize: '1.6rem' }}>{i18next.t('teleTherapy.totalTime')}</div>
          <div className="fw-bold" style={{ fontSize: '2.6rem' }}>{(total?.totalHours.toFixed(2) ?? 0)}h</div>
        </div>
      </OverlayTrigger>

      {renderChart()}
    </div>
  );
};

export default memo(TeleTherapy);
